import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Input, ModalShell } from '@/presentation/components/common';
import { useMyTenantMemberships } from '@/presentation/hooks/useMyTenantMemberships';
import { useTenants } from '@/presentation/hooks/useTenants';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import type { TenantGridDTO, TenantMembershipDTO } from '@/domain/repositories/ITenantRepository';

interface ChangeTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangeTenantModal: React.FC<ChangeTenantModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    email,
    memberships,
    isLoading,
    busyId,
    loadMemberships,
    addMembership,
    updateMembership,
    deleteMembership,
    changeEmail,
    updateLoginPassword,
    hasCredentials,
    credentialsLocked,
  } = useMyTenantMemberships();
  const { tenants, isLoading: isTenantsLoading, refetch } = useTenants();
  const { updateUserEmail } = useAuth();
  const { success, error: showError } = useToast();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [pendingDelete, setPendingDelete] = useState<TenantMembershipDTO | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [password, setPassword] = useState('');
  const isBusy = isLoading || busyId != null;

  const usedTenantIds = useMemo(
    () => new Set(memberships.map((item) => String(item.tenantId))),
    [memberships]
  );

  const activeCount = memberships.filter((item) => item.activo).length;

  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setAdding(false);
      setPendingDelete(null);
      setPasswordOpen(false);
      setPassword('');
      return;
    }
    setEmailDraft('');
    refetch();
    loadMemberships()
      .then((result) => {
        setEmailDraft(result.email);
      })
      .catch(() => {
        showError(t('pages.tenants.membershipsLoadError'));
      });
    // Solo al abrir: evita recargar en cada render del toast/i18n.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleToggle = async (membership: TenantMembershipDTO, next: boolean) => {
    try {
      await updateMembership(membership.id, { activo: next }, email);
      success(t('pages.tenants.toggleActiveSuccess'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.toggleActiveError'));
    }
  };

  const handleChangeClinic = async (membership: TenantMembershipDTO, tenant: TenantGridDTO) => {
    try {
      await updateMembership(membership.id, { tenantId: tenant.id }, email);
      setEditingId(null);
      success(t('pages.tenants.membershipClinicChanged'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.membershipClinicChangeError'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMembership(pendingDelete.id, email);
      if (editingId === pendingDelete.id) {
        setEditingId(null);
      }
      setPendingDelete(null);
      success(t('pages.tenants.deleteMembershipSuccess'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.deleteMembershipError'));
    }
  };

  const handleAddClinic = async (tenant: TenantGridDTO) => {
    try {
      await addMembership(tenant.id, email);
      setAdding(false);
      success(t('pages.tenants.addClinicSuccess'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.addClinicError'));
    }
  };

  const handleLoadEmail = async () => {
    const next = emailDraft.trim();
    if (!next) {
      showError(t('pages.tenants.membershipEmailInvalid'));
      return;
    }
    try {
      await loadMemberships(next);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.membershipsLoadError'));
    }
  };

  const closePasswordModal = () => {
    setPasswordOpen(false);
    setPassword('');
  };

  const handleSavePassword = async () => {
    if (!email) {
      showError(t('pages.tenants.membershipEmailInvalid'));
      return;
    }
    if (password.length < 6) {
      showError(t('pages.tenants.loginPasswordMin'));
      return;
    }
    try {
      const result = await updateLoginPassword(email, password, false);
      closePasswordModal();
      success(
        result.created
          ? t('pages.tenants.loginPasswordCreated')
          : t('pages.tenants.loginPasswordUpdated')
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.loginPasswordError'));
    }
  };

  const handleSaveEmail = async () => {
    const next = emailDraft.trim();
    if (!next) {
      showError(t('pages.tenants.membershipEmailInvalid'));
      return;
    }
    try {
      const result = await changeEmail(email || next, next);
      setEmailDraft(result.email);
      updateUserEmail(result.email);
      success(t('pages.tenants.membershipEmailChanged'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.membershipEmailChangeError'));
    }
  };

  return (
    <>
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnEscape={!passwordOpen}
      title={
        <span className="inline-flex items-center gap-2">
          <svg className="h-5 w-5 text-teal-600 dark:text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M4 10a8 8 0 0112-6m4 10a8 8 0 01-12 6" />
          </svg>
          {t('pages.tenants.myClinics')}
        </span>
      }
      subtitle={t('pages.tenants.activeCount', { active: activeCount, total: memberships.length })}
      footer={
        <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('pages.tenants.membershipsHelp')}
        </p>

        <div>
          <label htmlFor="membershipEmail" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
            {t('pages.tenants.membershipEmail')}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="membershipEmail"
              type="email"
              value={emailDraft}
              onChange={(event) => setEmailDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleLoadEmail();
                }
              }}
              disabled={isBusy}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <div className="flex shrink-0 gap-2">
              <Button variant="secondary" size="sm" onClick={() => void handleLoadEmail()} disabled={isBusy}>
                {t('pages.tenants.loadMembershipEmail')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSaveEmail()}
                disabled={isBusy || !emailDraft.trim() || emailDraft.trim().toLowerCase() === email.toLowerCase()}
                isLoading={busyId === 'email'}
              >
                {t('pages.tenants.saveMembershipEmail')}
              </Button>
            </div>
          </div>
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            {t('pages.tenants.membershipEmailHint')}
          </p>
        </div>

        {email ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {t('pages.tenants.loginPasswordTitle')}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t('pages.tenants.loginPasswordHint', { email })}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    hasCredentials
                      ? credentialsLocked
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                  }`}
                >
                  {hasCredentials
                    ? credentialsLocked
                      ? t('pages.tenants.loginPasswordLocked')
                      : t('pages.tenants.loginPasswordExists')
                    : t('pages.tenants.loginPasswordMissing')}
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setPasswordOpen(true)}
                  disabled={isBusy || !hasCredentials}
                >
                  {t('pages.tenants.loginPasswordChange')}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="max-h-[22rem] space-y-2 overflow-y-auto pr-1">
          {isLoading && memberships.length === 0 ? (
            <div className="rounded-lg border border-slate-200 px-4 py-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
              {t('common.loading')}
            </div>
          ) : memberships.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
              {t('pages.tenants.membershipsEmpty')}
            </div>
          ) : (
            memberships.map((membership) => {
              const rowBusy = busyId === membership.id;

              return (
                <div
                  key={membership.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {membership.tenantName || t('pages.tenants.noClinic')}
                        </p>
                        {membership.isOwner && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            {t('pages.tenants.owner')}
                          </span>
                        )}
                      </div>
                      {membership.tenantEmailAdministrador ? (
                        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                          {t('pages.tenants.clinicEmail')}: {membership.tenantEmailAdministrador}
                        </p>
                      ) : null}
                    </div>
                    <EnableSwitch
                      on={membership.activo}
                      disabled={rowBusy}
                      onChange={(next) => handleToggle(membership, next)}
                      onLabel={t('pages.tenants.active')}
                      offLabel={t('pages.tenants.inactive')}
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditingId((prev) => (prev === membership.id ? null : membership.id))}
                      disabled={rowBusy}
                    >
                      {t('pages.tenants.changeMembershipClinic')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setPendingDelete(membership)}
                      disabled={rowBusy}
                    >
                      {t('pages.tenants.deleteMembership')}
                    </Button>
                  </div>

                  {editingId === membership.id && (
                    <div className="mt-3">
                      <ClinicPicker
                        tenants={tenants}
                        isLoading={isTenantsLoading}
                        disabledIds={usedTenantIds}
                        disabled={rowBusy}
                        placeholder={t('pages.tenants.searchTenantPlaceholder')}
                        onSelect={(tenant) => handleChangeClinic(membership, tenant)}
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-slate-200 pt-3 dark:border-gray-700">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {t('pages.tenants.addClinic')}
            </p>
            {!adding && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setAdding(true)}
                disabled={isBusy}
              >
                {t('pages.tenants.addClinic')}
              </Button>
            )}
          </div>
          {adding && (
            <div className="space-y-2">
              <ClinicPicker
                tenants={tenants}
                isLoading={isTenantsLoading}
                disabledIds={usedTenantIds}
                disabled={busyId === 'add'}
                placeholder={t('pages.tenants.searchTenantPlaceholder')}
                onSelect={handleAddClinic}
              />
              <Button variant="ghost" size="sm" onClick={() => setAdding(false)} disabled={busyId === 'add'}>
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
    <ConfirmModal
      isOpen={pendingDelete != null}
      onClose={() => setPendingDelete(null)}
      onConfirm={handleConfirmDelete}
      variant="danger"
      isLoading={pendingDelete != null && busyId === pendingDelete.id}
      title={t('pages.tenants.deleteMembershipTitle')}
      message={t('pages.tenants.deleteMembershipConfirm', {
        clinic: pendingDelete?.tenantName || pendingDelete?.tenantId || '',
      })}
      confirmText={t('pages.tenants.deleteMembership')}
    />
    <ModalShell
      isOpen={passwordOpen}
      onClose={closePasswordModal}
      size="sm"
      zClass="z-[60]"
      title={t('pages.tenants.loginPasswordTitle')}
      subtitle={t('pages.tenants.loginPasswordHint', { email })}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={closePasswordModal}
            disabled={busyId === 'password'}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSavePassword()}
            disabled={isBusy || !password}
            isLoading={busyId === 'password'}
            className="w-full sm:w-auto"
          >
            {t('pages.tenants.loginPasswordSave')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSavePassword();
        }}
      >
        <Input
          label={t('pages.tenants.loginPasswordNew')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={isBusy}
          autoComplete="new-password"
          autoFocus
        />
      </form>
    </ModalShell>
    </>
  );
};

function EnableSwitch({
  on,
  disabled,
  onChange,
  onLabel,
  offLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  onLabel: string;
  offLabel: string;
}) {
  return (
    <label className="inline-flex items-center gap-2">
      <span
        className={`text-xs font-semibold ${
          on ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'
        }`}
      >
        {on ? onLabel : offLabel}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          on ? 'bg-teal-600' : 'bg-slate-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`mt-0.5 inline-block h-5 w-5 rounded-full bg-white transition-transform ${
            on ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function ClinicPicker({
  tenants,
  isLoading,
  disabledIds,
  disabled,
  placeholder,
  onSelect,
}: {
  tenants: TenantGridDTO[];
  isLoading: boolean;
  disabledIds: Set<string>;
  disabled?: boolean;
  placeholder: string;
  onSelect: (tenant: TenantGridDTO) => void;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const results = useMemo(() => {
    const search = term.toLowerCase().trim();
    const pool = search
      ? tenants.filter((tenant) => (
        tenant.nombre.toLowerCase().includes(search) ||
        tenant.emailAdministrador.toLowerCase().includes(search) ||
        String(tenant.id).toLowerCase().includes(search)
      ))
      : tenants;
    return pool.slice(0, 40);
  }, [tenants, term]);

  return (
    <div className="relative">
      <input
        type="text"
        className="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        placeholder={placeholder}
        value={term}
        disabled={disabled}
        autoComplete="off"
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimeoutRef.current = window.setTimeout(() => setOpen(false), 180);
        }}
      />
      {open && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t('pages.tenants.noTenantsFound')}</div>
          ) : (
            results.map((tenant) => {
              const already = disabledIds.has(String(tenant.id));
              return (
                <button
                  key={String(tenant.id)}
                  type="button"
                  disabled={already || disabled}
                  onClick={() => {
                    if (already) return;
                    onSelect(tenant);
                    setTerm('');
                    setOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm ${
                    already
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white">{tenant.nombre}</div>
                  {tenant.emailAdministrador ? (
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">{tenant.emailAdministrador}</div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
